import type { PropertySetItemProperty } from './PropertySetItem';
import type {
  CustomEntityPropertySetsInput,
  CustomPanelProperty,
  CustomPropertySetInput,
  DisplaySet,
} from './object-properties.types';
import {
  getVariesLabel,
  type ObjectPropertyFormattingContext,
} from './object-properties-formatting-helper';
import {
  customDefinitionKey,
  identityName,
  toCustomPropertySetItem,
  type BuildDisplaySetsContext,
} from './build-display-sets';
import {
  encodeCustomPropertyFavoriteKey,
  toDisplaySet,
} from './object-properties-panel-helper';

// bigint is not JSON-serializable, so tag it explicitly for value comparison.
// null and undefined are both "no value" (format as "--") and must compare equal.
const EMPTY_CUSTOM_VALUE_TOKEN = '\u0000empty';

const serializeCustomValue = (value: CustomPanelProperty['value']): string => {
  if (value === undefined || value === null) return EMPTY_CUSTOM_VALUE_TOKEN;
  return JSON.stringify(value, (_key, val: unknown) =>
    typeof val === 'bigint' ? `${val}n` : val
  );
};

const indexPropertiesByKey = (
  pset: CustomPropertySetInput
): Map<string, CustomPanelProperty> => {
  const byKey = new Map<string, CustomPanelProperty>();
  for (const prop of pset.properties) {
    const propKey = prop.id ?? prop.name;
    if (!byKey.has(propKey)) byKey.set(propKey, prop);
  }
  return byKey;
};

const aggregateSharedCustomProps = (
  representative: CustomPropertySetInput,
  instances: readonly CustomPropertySetInput[],
  formatting: ObjectPropertyFormattingContext
): PropertySetItemProperty[] => {
  const instanceMaps = instances.map(indexPropertiesByKey);
  const result: PropertySetItemProperty[] = [];

  for (const prop of representative.properties) {
    const propKey = prop.id ?? prop.name;
    const perInstance = instanceMaps.map((map) => map.get(propKey));
    const present = perInstance.filter(
      (candidate): candidate is CustomPanelProperty => candidate !== undefined
    );
    // Mirror model-common behaviour: drop properties not present on every
    // selected object.
    if (present.length !== instanceMaps.length) continue;

    const favoriteKey = encodeCustomPropertyFavoriteKey(
      representative.libId,
      representative.defId,
      prop.id ?? prop.name
    );

    const firstSerialized = serializeCustomValue(present[0].value);
    const sameAcrossSelection = present.every(
      (candidate) => serializeCustomValue(candidate.value) === firstSerialized
    );

    if (!sameAcrossSelection) {
      result.push({
        key: favoriteKey,
        name: prop.name,
        value: getVariesLabel(),
        muted: true,
      });
      continue;
    }

    result.push(
      toCustomPropertySetItem(
        present[0],
        representative.libId,
        representative.defId,
        formatting
      )
    );
  }

  return result;
};

/**
 * @intent Builds the custom property sets shared by every selected object for
 * the multi-selection view. A custom set (identified by `libId::defId`) is kept
 * only when present on all selected entities, matching how model-common
 * properties are intersected. Within a shared set, a property is dropped unless
 * present on every object, and rendered as "Varies" (muted) when its value
 * differs across the selection.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 16 > 15 allowed; flagged for later refactor, not fixed here
export function buildSharedCustomSets(
  selectedEntityIds: readonly string[],
  customEntityPropertySets: readonly CustomEntityPropertySetsInput[],
  ctx: BuildDisplaySetsContext
): DisplaySet[] {
  if (selectedEntityIds.length === 0) return [];

  const definitionsByEntity = new Map<
    string,
    Map<string, CustomPropertySetInput>
  >();
  for (const entry of customEntityPropertySets) {
    const byDefinition = new Map<string, CustomPropertySetInput>();
    for (const pset of entry.psets) {
      const defKey = customDefinitionKey(pset);
      if (!byDefinition.has(defKey)) byDefinition.set(defKey, pset);
    }
    definitionsByEntity.set(entry.entityId, byDefinition);
  }

  // A shared set must exist on the first selected object, so its definitions
  // are the complete candidate list (in a stable order).
  const firstEntityDefinitions = definitionsByEntity.get(selectedEntityIds[0]);
  if (firstEntityDefinitions === undefined) return [];

  const sharedSets: DisplaySet[] = [];
  for (const [defKey, representative] of firstEntityDefinitions) {
    const instances: CustomPropertySetInput[] = [];
    let sharedByAll = true;
    for (const entityId of selectedEntityIds) {
      const pset = definitionsByEntity.get(entityId)?.get(defKey);
      if (pset === undefined) {
        sharedByAll = false;
        break;
      }
      instances.push(pset);
    }
    if (!sharedByAll) continue;

    sharedSets.push(
      toDisplaySet({
        psetName: representative.name,
        setKey: defKey,
        isCustom: true,
        properties: aggregateSharedCustomProps(
          representative,
          instances,
          ctx.formattingContext
        ),
        formatName: identityName,
        sortPropertiesByName: ctx.sortPropertiesByName,
      })
    );
  }

  return sharedSets;
}
